import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const CAT_OPTIONS = [
  { value: 'ecole', label: 'École', emoji: '🏫' },
  { value: 'activite', label: 'Activité extra-scolaire', emoji: '⚽' },
  { value: 'medical', label: 'Médical / RDV', emoji: '🩺' },
  { value: 'anniversaire', label: 'Anniversaire', emoji: '🎂' },
  { value: 'autre', label: 'Autre', emoji: '📌' },
];

const ORGANIZE_OPTIONS = [
  { value: 'by_child', label: '👶 Par enfant (cartes)' },
  { value: 'date_asc', label: '📅 Par date (la plus proche d\'abord)' },
  { value: 'category', label: '🏷️ Par catégorie' },
  { value: 'alpha_title', label: '🔤 Par titre (A → Z)' },
];

function calcAge(birthDate) {
  if (!birthDate) return '';
  const bd = new Date(birthDate);
  const diff = Date.now() - bd.getTime();
  const age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  return age + ' ans';
}

export default function ChildrenPage() {
  const [children, setChildren] = useState([]);
  const [activities, setActivities] = useState({});
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [showActModal, setShowActModal] = useState(null); // { childId, editingActId? }
  const [childForm, setChildForm] = useState({ name: '', birth_date: '', school: '', color: '#f472b6' });
  const [actForm, setActForm] = useState({ title: '', category: 'autre', activity_date: '', start_time: '', end_time: '', recurring: 'none', notes: '', child_id: '' });
  const [organizeBy, setOrganizeBy] = useState(() => localStorage.getItem('children_organize_by') || 'by_child');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => { localStorage.setItem('children_organize_by', organizeBy); }, [organizeBy]);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/children');
      setChildren(res.data);
      const acts = {};
      for (const child of res.data) {
        const r = await api.get(`/children/${child.id}/activities`);
        acts[child.id] = r.data;
      }
      setActivities(acts);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreateChild = () => {
    setEditingChildId(null);
    setChildForm({ name: '', birth_date: '', school: '', color: '#f472b6' });
    setShowChildModal(true);
  };

  const openEditChild = (child) => {
    setEditingChildId(child.id);
    setChildForm({
      name: child.name,
      birth_date: child.birth_date ? child.birth_date.split('T')[0] : '',
      school: child.school || '',
      color: child.color || '#f472b6',
    });
    setShowChildModal(true);
  };

  const saveChild = async (e) => {
    e.preventDefault();
    if (editingChildId) {
      await api.put(`/children/${editingChildId}`, childForm);
    } else {
      await api.post('/children', childForm);
    }
    setShowChildModal(false);
    setEditingChildId(null);
    load();
  };

  const deleteChild = async (id) => {
    if (!window.confirm('Supprimer cet enfant et toutes ses activités ?')) return;
    await api.delete(`/children/${id}`);
    load();
  };

  const openCreateActivity = (childId) => {
    setShowActModal({ childId, editingActId: null });
    setActForm({ title: '', category: 'autre', activity_date: new Date().toISOString().split('T')[0], start_time: '', end_time: '', recurring: 'none', notes: '', child_id: childId });
  };

  const openEditActivity = (act) => {
    setShowActModal({ childId: act.child_id, editingActId: act.id });
    setActForm({
      title: act.title,
      category: act.category,
      activity_date: act.activity_date ? act.activity_date.split('T')[0] : '',
      start_time: act.start_time || '',
      end_time: act.end_time || '',
      recurring: act.recurring || 'none',
      notes: act.notes || '',
      child_id: act.child_id,
    });
  };

  const saveActivity = async (e) => {
    e.preventDefault();
    const { editingActId } = showActModal;
    // On privilégie l'enfant du formulaire (qui peut avoir été changé via le dropdown)
    const targetChildId = actForm.child_id || showActModal.childId;
    if (!targetChildId) {
      alert('Veuillez sélectionner un enfant');
      return;
    }
    try {
      if (editingActId) {
        await api.put(`/children/activities/${editingActId}`, actForm);
      } else {
        await api.post(`/children/${targetChildId}/activities`, actForm);
      }
      setShowActModal(null);
      setActForm({ title: '', category: 'autre', activity_date: '', start_time: '', end_time: '', recurring: 'none', notes: '', child_id: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteActivity = async (actId) => {
    if (!window.confirm('Supprimer cette activité ?')) return;
    await api.delete(`/children/activities/${actId}`);
    load();
  };

  // Liste unifiée de toutes les activités avec info enfant
  const allActivities = React.useMemo(() => {
    const list = [];
    const todayStr = new Date().toISOString().split('T')[0];
    children.forEach(child => {
      (activities[child.id] || []).forEach(act => {
        if (!showPast && act.activity_date < todayStr) return;
        list.push({ ...act, child_name: child.name, child_color: child.color });
      });
    });
    return list;
  }, [children, activities, showPast]);

  // Organisation selon le mode choisi
  const organized = React.useMemo(() => {
    if (organizeBy === 'by_child') return null; // rendu par cartes

    // Groupement par DATE
    if (organizeBy === 'date_asc') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      const retard = { key: 'retard', label: 'Passées récemment', emoji: '🕓', items: [], color: 'var(--text-muted)' };
      const todayB = { key: 'today', label: "Aujourd'hui", emoji: '☀️', items: [], color: 'var(--primary)' };
      const tomorrowB = { key: 'tomorrow', label: 'Demain', emoji: '➡️', items: [], color: 'var(--text)' };
      const nodate = { key: 'nodate', label: 'Sans date', emoji: '⏳', items: [], color: 'var(--text-muted)' };
      const futureByDate = {};

      allActivities.forEach(a => {
        if (!a.activity_date) { nodate.items.push(a); return; }
        const d = a.activity_date.split('T')[0];
        if (d < todayStr) retard.items.push(a);
        else if (d === todayStr) todayB.items.push(a);
        else if (d === tomorrowStr) tomorrowB.items.push(a);
        else {
          if (!futureByDate[d]) {
            const dt = new Date(d + 'T00:00');
            const label = dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            futureByDate[d] = { key: d, label: label.charAt(0).toUpperCase() + label.slice(1), emoji: '📅', items: [], color: 'var(--text)' };
          }
          futureByDate[d].items.push(a);
        }
      });

      const sortInternal = (a, c) => {
        if (a.start_time && c.start_time) return a.start_time.localeCompare(c.start_time);
        if (a.start_time) return -1;
        if (c.start_time) return 1;
        return (a.title || '').localeCompare(c.title || '', 'fr');
      };
      [retard, todayB, tomorrowB, nodate, ...Object.values(futureByDate)].forEach(b => b.items.sort(sortInternal));

      return [
        ...(retard.items.length && showPast ? [retard] : []),
        ...(todayB.items.length ? [todayB] : []),
        ...(tomorrowB.items.length ? [tomorrowB] : []),
        ...Object.values(futureByDate).sort((a, b) => a.key.localeCompare(b.key)),
        ...(nodate.items.length ? [nodate] : []),
      ];
    }

    // Groupement par CATÉGORIE
    if (organizeBy === 'category') {
      const groups = {};
      allActivities.forEach(a => {
        if (!groups[a.category]) groups[a.category] = [];
        groups[a.category].push(a);
      });
      const order = ['ecole', 'activite', 'medical', 'anniversaire', 'autre'];
      return order.filter(cat => groups[cat]).map(cat => {
        const opt = CAT_OPTIONS.find(c => c.value === cat);
        return {
          key: cat,
          label: opt?.label || cat,
          emoji: opt?.emoji || '📌',
          items: groups[cat].sort((a, b) => (a.activity_date || '').localeCompare(b.activity_date || '')),
          color: 'var(--text)',
        };
      });
    }

    // Tri alphabétique (liste plate)
    if (organizeBy === 'alpha_title') {
      const sorted = [...allActivities].sort((a, c) => (a.title || '').localeCompare(c.title || '', 'fr'));
      return [{ key: 'all', label: 'Toutes les activités', emoji: '🔤', items: sorted, color: 'var(--text)' }];
    }

    return null;
  }, [organizeBy, allActivities, showPast]);

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Enfants</h1>
          <p>{children.length} enfant{children.length !== 1 ? 's' : ''} · {allActivities.length} activité{allActivities.length !== 1 ? 's' : ''}{showPast ? ' (passées incluses)' : ' à venir'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {children.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={() => openCreateActivity(children[0].id)}
              title="Ajouter une activité à un enfant"
            >
              + Activité
            </button>
          )}
          <button className="btn btn-secondary" onClick={openCreateChild}>+ Enfant</button>
        </div>
      </div>

      {children.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">👶</div>
          <p>Ajoutez vos enfants pour gérer leurs activités, école et rendez-vous.</p>
        </div>
      ) : (
        <>
          {/* Sélecteur d'organisation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)' }}>Organiser :</label>
            <select
              className="form-input"
              style={{ maxWidth: 300, fontSize: '0.85rem', fontWeight: 600 }}
              value={organizeBy}
              onChange={e => setOrganizeBy(e.target.value)}
            >
              {ORGANIZE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {organizeBy !== 'by_child' && (
              <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-light)', cursor: 'pointer' }}>
                <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} />
                Inclure les activités passées
              </label>
            )}
          </div>

          {/* VUE PAR ENFANT (cartes) */}
          {organizeBy === 'by_child' && (
            <div className="children-grid">
              {children.map(child => (
                <div key={child.id} className="child-card">
                  <div className="child-card-accent" style={{ background: child.color }} />
                  <div className="child-card-header">
                    <div style={{ cursor: 'pointer' }} onClick={() => openEditChild(child)} title="Cliquer pour modifier">
                      <div className="child-name">{child.name} <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>✏️</span></div>
                      {child.school && <div className="child-school">🏫 {child.school}</div>}
                      {child.birth_date && <div className="child-age">{calcAge(child.birth_date)}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-xs btn-secondary" onClick={() => openCreateActivity(child.id)}>+ Activité</button>
                      <button className="btn-ghost" onClick={() => deleteChild(child.id)}>🗑️</button>
                    </div>
                  </div>

                  <div className="child-activities">
                    {(activities[child.id] || []).filter(a => a.activity_date >= new Date().toISOString().split('T')[0]).slice(0, 8).map(act => (
                      <div key={act.id} className="child-activity-item" style={{ cursor: 'pointer' }} onClick={() => openEditActivity(act)} title="Cliquer pour modifier">
                        <span className={`badge badge-${act.category}`}>{CAT_OPTIONS.find(c => c.value === act.category)?.label || act.category}</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>{act.title}</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {new Date(act.activity_date + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          {act.start_time && ' · ' + act.start_time.slice(0, 5)}
                        </span>
                        <button className="btn-ghost" style={{ fontSize: '0.7rem' }} onClick={e => { e.stopPropagation(); deleteActivity(act.id); }}>✕</button>
                      </div>
                    ))}
                    {(!activities[child.id] || activities[child.id].filter(a => a.activity_date >= new Date().toISOString().split('T')[0]).length === 0) && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '8px 0' }}>Aucune activité à venir</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* VUE GROUPÉE (date / catégorie / alpha) */}
          {organized && organized.length === 0 && (
            <div className="empty-state">
              <div className="empty-emoji">📭</div>
              <p>Aucune activité à afficher dans ce mode.</p>
            </div>
          )}
          {organized && organized.length > 0 && (
            <div>
              {organized.map(group => (
                <div key={group.key} style={{ marginBottom: 20 }}>
                  <div className="day-section-title" style={{ color: group.color }}>
                    <span className="section-emoji">{group.emoji}</span>
                    {group.label}
                    <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                      {group.items.length} activité{group.items.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {group.items.map(act => (
                      <div key={act.id} className="task-item" style={{ cursor: 'pointer' }} onClick={() => openEditActivity(act)} title="Cliquer pour modifier">
                        <div style={{ width: 6, height: 36, borderRadius: 3, background: act.child_color, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{act.title}</div>
                          <div className="task-meta">
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: act.child_color }}>👶 {act.child_name}</span>
                            <span className={`badge badge-${act.category}`}>
                              {CAT_OPTIONS.find(c => c.value === act.category)?.emoji} {CAT_OPTIONS.find(c => c.value === act.category)?.label}
                            </span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                              📅 {new Date(act.activity_date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {act.start_time && ' · ' + act.start_time.slice(0, 5)}
                            </span>
                            {act.recurring !== 'none' && <span className="badge" style={{ background: '#f0fdf4', color: 'var(--success)' }}>🔄 {act.recurring}</span>}
                          </div>
                          {act.notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: 4, fontStyle: 'italic' }}>{act.notes}</div>}
                        </div>
                        <button className="btn-ghost" onClick={e => { e.stopPropagation(); deleteActivity(act.id); }}>🗑️</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal enfant (ajout ou modification) */}
      {showChildModal && (
        <div className="modal-overlay" /* Pas de fermeture au clic extérieur */>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{editingChildId ? 'Modifier l\'enfant' : 'Ajouter un enfant'}</h2>
              <button type="button" className="btn-icon" title="Fermer" onClick={() => setShowChildModal(false)}>✕</button>
            </div>
            <form onSubmit={saveChild}>
              <div className="form-group">
                <label>Prénom</label>
                <input className="form-input" value={childForm.name} onChange={e => setChildForm({ ...childForm, name: e.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input className="form-input" type="date" value={childForm.birth_date} onChange={e => setChildForm({ ...childForm, birth_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Couleur</label>
                  <input type="color" value={childForm.color} onChange={e => setChildForm({ ...childForm, color: e.target.value })} style={{ width: 50, height: 38, border: 'none', cursor: 'pointer' }} />
                </div>
              </div>
              <div className="form-group">
                <label>École / Crèche</label>
                <input className="form-input" value={childForm.school} onChange={e => setChildForm({ ...childForm, school: e.target.value })} placeholder="Nom de l'établissement" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowChildModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingChildId ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal activité (ajout ou modification) */}
      {showActModal && (
        <div className="modal-overlay" /* Pas de fermeture au clic extérieur */>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>
                {showActModal.editingActId ? 'Modifier l\'activité' : 'Nouvelle activité'}
              </h2>
              <button type="button" className="btn-icon" title="Fermer" onClick={() => setShowActModal(null)}>✕</button>
            </div>
            <form onSubmit={saveActivity}>
              {/* Sélecteur d'enfant (toujours visible : création + édition) */}
              <div className="form-group">
                <label>Enfant concerné *</label>
                <select
                  className="form-input"
                  value={actForm.child_id || showActModal.childId || ''}
                  onChange={e => {
                    const id = parseInt(e.target.value);
                    setActForm({ ...actForm, child_id: id });
                    if (!showActModal.editingActId) {
                      setShowActModal({ ...showActModal, childId: id });
                    }
                  }}
                  required
                >
                  {children.map(c => <option key={c.id} value={c.id}>👶 {c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Titre</label>
                <input className="form-input" value={actForm.title} onChange={e => setActForm({ ...actForm, title: e.target.value })} placeholder="Ex: Cours de natation, RDV pédiatre..." required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select className="form-input" value={actForm.category} onChange={e => setActForm({ ...actForm, category: e.target.value })}>
                    {CAT_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Récurrence</label>
                  <select className="form-input" value={actForm.recurring} onChange={e => setActForm({ ...actForm, recurring: e.target.value })}>
                    <option value="none">Unique</option>
                    <option value="weekly">Chaque semaine</option>
                    <option value="monthly">Chaque mois</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input className="form-input" type="date" value={actForm.activity_date} onChange={e => setActForm({ ...actForm, activity_date: e.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Début</label>
                  <input className="form-input" type="time" value={actForm.start_time} onChange={e => setActForm({ ...actForm, start_time: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fin</label>
                  <input className="form-input" type="time" value={actForm.end_time} onChange={e => setActForm({ ...actForm, end_time: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input className="form-input" value={actForm.notes} onChange={e => setActForm({ ...actForm, notes: e.target.value })} placeholder="Infos complémentaires..." />
              </div>
              <div className="modal-actions">
                {showActModal.editingActId && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    style={{ marginRight: 'auto' }}
                    onClick={() => { deleteActivity(showActModal.editingActId); setShowActModal(null); }}
                  >
                    🗑️ Supprimer
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowActModal(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{showActModal.editingActId ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
